import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";
import { parseApiError } from "../../lib/http/apiErrors";
import { env } from "../../config/env";
import { formatISTDateTime } from "../../utils/dateUtils";
import {
  fetchInvoiceSettings,
  fetchInvoices,
  exportInvoicesCsv,
  resendInvoice,
  updateInvoiceSettings,
  voidInvoice,
  type Invoice,
  type InvoiceDeliveryAttempt,
  type InvoiceDeliveryChannel,
  type InvoiceKind,
  type InvoiceSettings,
  type InvoiceStatus,
} from "../../features/admin/billingApi";

/** Billing → Invoices: the company's GST invoice settings, plus every GST
 *  invoice/receipt issued for a payment (see backend/app/modules/invoices). */

const input = "w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500";
const labelCls = "mb-1 block text-xs font-medium text-gray-400";

const KIND_LABEL: Record<InvoiceKind, string> = {
  subscription: "Subscription",
  upgrade: "Upgrade",
  topup: "Top-up",
};

function formatMoney(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format((minor ?? 0) / 100);
  } catch {
    return `${currency} ${((minor ?? 0) / 100).toFixed(2)}`;
  }
}

function Toggle({
  checked, onChange, disabled,
}: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${checked ? "bg-indigo-600" : "bg-gray-700"}`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

const DELIVERY_BADGE_COLOR: Record<InvoiceDeliveryAttempt["status"], string> = {
  queued: "bg-gray-800 text-gray-400",
  sending: "bg-gray-800 text-gray-400",
  pending: "bg-gray-800 text-gray-400",
  sent: "bg-emerald-900/40 text-emerald-300",
  skipped: "bg-gray-800 text-gray-500",
  failed: "bg-red-900/40 text-red-300",
};

const DELIVERY_BADGE_SYMBOL: Record<InvoiceDeliveryAttempt["status"], string> = {
  queued: "…",
  sending: "…",
  pending: "…",
  sent: "✓",
  skipped: "–",
  failed: "✗",
};

/** "7, 1" <-> [7, 1]. Edits locally and commits on blur, so typing a comma
 *  isn't swallowed by re-formatting. */
function DaysInput({ value, onChange, placeholder }: {
  value: number[]; onChange: (v: number[]) => void; placeholder: string;
}) {
  const [text, setText] = useState(value.join(", "));
  useEffect(() => setText(value.join(", ")), [value]);
  return (
    <input className={input} value={text} placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const days = Array.from(new Set(
          text.split(/[\s,]+/).map((t) => Number(t)).filter((n) => Number.isInteger(n) && n >= 1 && n <= 30),
        )).sort((a, b) => b - a);
        onChange(days);
        setText(days.join(", "));
      }} />
  );
}

function DeliveryBadge({ label, attempt }: { label: string; attempt?: InvoiceDeliveryAttempt }) {
  if (!attempt) return null;
  const title = attempt.error || (attempt.at ? formatISTDateTime(attempt.at) : attempt.status);
  return (
    <span
      title={title}
      className={`mr-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${DELIVERY_BADGE_COLOR[attempt.status]}`}
    >
      {label} {DELIVERY_BADGE_SYMBOL[attempt.status]}
    </span>
  );
}

function ResendMenu({ invoice, onSent }: { invoice: Invoice; onSent: () => void }) {
  const { showError, showSuccess } = useAdminToast();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(true);
  const [whatsapp, setWhatsapp] = useState(false);
  const [sending, setSending] = useState(false);

  const send = async () => {
    const channels: InvoiceDeliveryChannel[] = [
      ...(email ? (["email"] as const) : []),
      ...(whatsapp ? (["whatsapp"] as const) : []),
    ];
    if (channels.length === 0) {
      showError("Pick at least one channel.");
      return;
    }
    setSending(true);
    try {
      const res = await resendInvoice(invoice.id, channels);
      showSuccess(res.queued.length > 0 ? `Queued: ${res.queued.join(", ")}` : "Nothing queued (already queued or channel unavailable)");
      setOpen(false);
      onSent();
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <span className="relative inline-block">
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs text-indigo-400 hover:text-indigo-300">
        Resend
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-2 w-56 rounded-lg border border-gray-700 bg-gray-800 p-3 text-left shadow-lg">
          <label className="flex items-center gap-2 py-1 text-xs text-gray-200">
            <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} className="accent-indigo-500" />
            Email
          </label>
          <label className="flex items-center gap-2 py-1 text-xs text-gray-200">
            <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} className="accent-indigo-500" />
            WhatsApp
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="rounded px-2 py-1 text-xs text-gray-400 hover:text-white">
              Cancel
            </button>
            <button type="button" onClick={() => void send()} disabled={sending}
              className="rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-500 disabled:opacity-50">
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

function SettingsCard({
  settings, stateCodes, onSaved,
}: { settings: InvoiceSettings; stateCodes: Record<string, string>; onSaved: (s: InvoiceSettings) => void }) {
  const { showError, showSuccess } = useAdminToast();
  const [draft, setDraft] = useState<InvoiceSettings>(settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => setDraft(settings), [settings]);

  const set = <K extends keyof InvoiceSettings>(k: K, v: InvoiceSettings[K]) => setDraft((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const { enabled_at: _enabled_at, ...body } = draft;
      const res = await updateInvoiceSettings(body);
      onSaved(res.settings);
      showSuccess("Invoice settings saved.");
    } catch (err) {
      const msg = parseApiError(err).message;
      setError(msg);
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-8 space-y-5 rounded-xl border border-gray-800 bg-gray-900 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-white">Company invoice settings</h2>
          <p className="mt-1 text-xs text-gray-500">Your GST details, printed on every invoice and receipt.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`rounded px-2 py-0.5 text-xs font-medium ${draft.enabled ? "bg-emerald-900/40 text-emerald-300" : "bg-gray-800 text-gray-500"}`}>
            {draft.enabled ? "ON" : "OFF"}
          </span>
          <Toggle checked={draft.enabled} onChange={(v) => set("enabled", v)} />
        </div>
      </div>

      {!settings.enabled && (
        <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-300">
          Invoicing is OFF — no invoices are issued until you enable it.
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-800/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">{error}</div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Legal name</label>
          <input className={input} value={draft.legal_name} onChange={(e) => set("legal_name", e.target.value)} placeholder="Registered company name" />
        </div>
        <div>
          <label className={labelCls}>Trade name <span className="text-gray-600">(optional, shown on invoice)</span></label>
          <input className={input} value={draft.trade_name} onChange={(e) => set("trade_name", e.target.value)} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>GSTIN</label>
          <input className={`${input} font-mono uppercase`} value={draft.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" />
          <p className="mt-1 text-[11px] text-gray-500">Printed on every invoice.</p>
        </div>
        <div>
          <label className={labelCls}>PAN</label>
          <input className={`${input} font-mono uppercase`} value={draft.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} placeholder="AAAAA0000A" />
        </div>
      </div>

      <div>
        <label className={labelCls}>Address</label>
        <textarea className={`${input} h-20`} value={draft.address} onChange={(e) => set("address", e.target.value)} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>State</label>
          <select className={input} value={draft.state_code} onChange={(e) => set("state_code", e.target.value)}>
            <option value="">Select state…</option>
            {Object.entries(stateCodes).map(([code, name]) => (
              <option key={code} value={code}>{code} — {name}</option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-gray-500">Must match the first two digits of the GSTIN.</p>
        </div>
        <div>
          <label className={labelCls}>Support email <span className="text-gray-600">(shown on invoice)</span></label>
          <input className={input} type="email" value={draft.email} onChange={(e) => set("email", e.target.value)} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>SAC code</label>
          <input className={input} value={draft.sac_code} onChange={(e) => set("sac_code", e.target.value)} />
          <p className="mt-1 text-[11px] text-gray-500">Confirm with CA.</p>
        </div>
        <div>
          <label className={labelCls}>GST rate (%)</label>
          <input className={input} type="number" min={0} max={100} step="0.01"
            value={draft.gst_rate_pct}
            onChange={(e) => set("gst_rate_pct", e.target.value === "" ? 0 : Number(e.target.value))} />
        </div>
      </div>

      <div>
        <label className={labelCls}>LUT number <span className="text-gray-600">(optional)</span></label>
        <input className={input} value={draft.lut_number} onChange={(e) => set("lut_number", e.target.value)} />
        <p className="mt-1 text-[11px] text-gray-500">Set to zero-rate exports (foreign customers paying in USD).</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Invoice number prefix</label>
          <input className={`${input} font-mono uppercase`} value={draft.series_prefix}
            onChange={(e) => set("series_prefix", e.target.value.toUpperCase())} placeholder="INV" maxLength={3} />
        </div>
        <div>
          <label className={labelCls}>Receipt number prefix</label>
          <input className={`${input} font-mono uppercase`} value={draft.receipt_prefix}
            onChange={(e) => set("receipt_prefix", e.target.value.toUpperCase())} placeholder="RCP" maxLength={3} />
        </div>
      </div>

      <div className="border-t border-gray-800 pt-5">
        <h3 className="text-sm font-semibold text-white">Delivery</h3>
        <p className="mt-1 text-xs text-gray-500">How invoices reach customers after each payment.</p>

        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-200">Email invoices to customers</p>
              <p className="mt-0.5 text-xs text-gray-500">
                Sends &ldquo;Your receipt&rdquo; with invoice &amp; receipt download links after each payment.
              </p>
            </div>
            <Toggle checked={draft.email_enabled} onChange={(v) => set("email_enabled", v)} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-200">Attach invoice &amp; receipt PDFs to the email</p>
              <p className="mt-0.5 text-xs text-gray-500">
                Download links stay in the email either way. Turn on only after the email service with attachment
                support is deployed — the old one rejects emails this large.
              </p>
            </div>
            <Toggle checked={draft.email_attach_pdfs} onChange={(v) => set("email_attach_pdfs", v)} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-200">Send on WhatsApp</p>
              <p className="mt-0.5 text-xs text-gray-500">
                To every customer with a WhatsApp number, unless they switched it off. Needs a Meta-approved
                template — keep off until the template is approved.
              </p>
            </div>
            <Toggle checked={draft.whatsapp_enabled} onChange={(v) => set("whatsapp_enabled", v)} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-200">Send the invoice PDF itself on WhatsApp</p>
              <p className="mt-0.5 text-xs text-gray-500">
                Off: template <code>invoice_issued</code> with a &ldquo;Download invoice&rdquo; button. On: template{" "}
                <code>invoice_issued_doc</code> with the PDF attached — needs Baatly&rsquo;s per-message document
                support first.
              </p>
            </div>
            <Toggle checked={draft.whatsapp_attach_pdf} onChange={(v) => set("whatsapp_attach_pdf", v)} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={labelCls}>WhatsApp template name</label>
              <input className={input} value={draft.whatsapp_template}
                onChange={(e) => set("whatsapp_template", e.target.value)} placeholder="invoice_issued" />
            </div>
            <div>
              <label className={labelCls}>WhatsApp template language</label>
              <input className={input} value={draft.whatsapp_template_language}
                onChange={(e) => set("whatsapp_template_language", e.target.value)} placeholder="en_US" />
            </div>
          </div>

          <div className="max-w-xs">
            <label className={labelCls}>Download link validity (days)</label>
            <input className={input} type="number" min={1} max={90} value={draft.link_ttl_days}
              onChange={(e) => set("link_ttl_days", Math.min(90, Math.max(1, Number(e.target.value) || 1)))} />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800 pt-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-white">Payment reminders</h3>
            <p className="mt-1 text-xs text-gray-500">
              Renewal reminders, &ldquo;payment failed&rdquo; and &ldquo;plan ending&rdquo; notices by email (and WhatsApp when
              it&rsquo;s on). Only for paid subscriptions.
            </p>
          </div>
          <Toggle checked={draft.reminders_enabled} onChange={(v) => set("reminders_enabled", v)} />
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className={labelCls}>Yearly plans: remind days before renewal</label>
            <DaysInput value={draft.reminder_days_yearly} onChange={(v) => set("reminder_days_yearly", v)} placeholder="7, 1" />
          </div>
          <div>
            <label className={labelCls}>Monthly plans: remind days before renewal</label>
            <DaysInput value={draft.reminder_days_monthly} onChange={(v) => set("reminder_days_monthly", v)} placeholder="3" />
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div>
            <label className={labelCls}>WhatsApp template: renewal</label>
            <input className={input} value={draft.whatsapp_template_renewal}
              onChange={(e) => set("whatsapp_template_renewal", e.target.value)} placeholder="payment_upcoming" />
          </div>
          <div>
            <label className={labelCls}>WhatsApp template: payment failed</label>
            <input className={input} value={draft.whatsapp_template_failed}
              onChange={(e) => set("whatsapp_template_failed", e.target.value)} placeholder="payment_failed" />
          </div>
          <div>
            <label className={labelCls}>WhatsApp template: plan ending</label>
            <input className={input} value={draft.whatsapp_template_lapsing}
              onChange={(e) => set("whatsapp_template_lapsing", e.target.value)} placeholder="plan_lapsing" />
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          &ldquo;Plan ending&rdquo; goes 2 days before the grace period (SUBSCRIPTION_GRACE_PERIOD_DAYS) after a failed payment ends.
        </p>
      </div>

      <div>
        <button type="button" onClick={() => setAdvancedOpen((o) => !o)} className="text-xs text-gray-400 hover:text-white">
          {advancedOpen ? "Hide advanced ▲" : "Advanced ▾"}
        </button>
        {advancedOpen && (
          <div className="mt-3 max-w-xs">
            <label className={labelCls}>Minimum amount to invoice (minor units, e.g. paise)</label>
            <input className={input} inputMode="numeric" value={draft.min_amount_minor}
              onChange={(e) => set("min_amount_minor", Number(e.target.value.replace(/\D/g, "")) || 0)} />
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button onClick={() => void save()} disabled={saving}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

export default function AdminInvoicesPage() {
  const { showError, showSuccess } = useAdminToast();

  const [settings, setSettings] = useState<InvoiceSettings | null>(null);
  const [stateCodes, setStateCodes] = useState<Record<string, string>>({});

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [kind, setKind] = useState<"" | InvoiceKind>("");
  const [status, setStatus] = useState<"" | InvoiceStatus>("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const limit = 50;

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetchInvoiceSettings();
      setSettings(res.settings);
      setStateCodes(res.state_codes);
    } catch (err) {
      showError(parseApiError(err).message);
    }
  }, [showError]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchInvoices({
        q: debouncedQ || undefined,
        kind: kind || undefined,
        status: status || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        page,
        limit,
      });
      setInvoices(res.invoices);
      setTotal(res.total);
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, kind, status, fromDate, toDate, page, showError]);

  useEffect(() => { void loadSettings(); }, [loadSettings]);
  useEffect(() => { void loadInvoices(); }, [loadInvoices]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { setPage(1); }, [kind, status, fromDate, toDate]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadSettings(), loadInvoices()]);
  }, [loadSettings, loadInvoices]);

  const openPdf = (relativeUrl: string) => {
    window.open(`${env.apiBaseUrl}${relativeUrl}`, "_blank", "noopener,noreferrer");
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportInvoicesCsv({
        q: debouncedQ || undefined,
        kind: kind || undefined,
        status: status || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
      });
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setExporting(false);
    }
  };

  const cancelInvoice = async (inv: Invoice) => {
    const reason = window.prompt(`Reason for cancelling invoice ${inv.invoice_no}? (required)`);
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      showError("A cancellation reason is required.");
      return;
    }
    if (!window.confirm(`Cancel invoice ${inv.invoice_no}? The invoice number stays used; this does not refund the payment.`)) return;
    try {
      await voidInvoice(inv.id, trimmed);
      showSuccess("Invoice cancelled.");
      await loadInvoices();
    } catch (err) {
      showError(parseApiError(err).message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <AdminPageHeader
        title="Invoices"
        subtitle="Company GST invoice settings and every invoice/receipt issued for a payment."
        actions={<RefreshButton onRefresh={refreshAll} />}
      />

      {settings ? (
        <SettingsCard settings={settings} stateCodes={stateCodes} onSaved={setSettings} />
      ) : (
        <p className="mb-8 text-sm text-gray-500">Loading settings…</p>
      )}

      <div className="rounded-xl border border-gray-800 bg-gray-900">
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-800 p-4">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search invoice no / email / name / GSTIN…"
            className="min-w-[220px] max-w-sm flex-1 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-600 outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <select value={kind} onChange={(e) => setKind(e.target.value as "" | InvoiceKind)}
            className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All types</option>
            <option value="subscription">Subscription</option>
            <option value="upgrade">Upgrade</option>
            <option value="topup">Top-up</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value as "" | InvoiceStatus)}
            className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All statuses</option>
            <option value="issued">Issued</option>
            <option value="void">Cancelled</option>
          </select>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500" />
          <span className="text-xs text-gray-600">to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500" />
          <button onClick={() => void handleExport()} disabled={exporting}
            className="ml-auto rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 disabled:opacity-50">
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-left text-xs text-gray-500">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Invoice no</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 text-right font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Sent</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr><td colSpan={8} className="py-16 text-center text-gray-500">Loading…</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={8} className="py-16 text-center text-gray-500">No invoices found.</td></tr>
              ) : (
                invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-800/30">
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{formatISTDateTime(inv.issued_at)}</td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-white">{inv.invoice_no}</td>
                    <td className="px-4 py-3">
                      <p className="max-w-[200px] truncate text-sm text-white">{inv.buyer_name || "—"}</p>
                      <p className="max-w-[200px] truncate text-xs text-gray-500">{inv.buyer_email}</p>
                      {inv.buyer_gstin && <p className="font-mono text-xs text-gray-600">{inv.buyer_gstin}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">{KIND_LABEL[inv.kind] ?? inv.kind}</td>
                    <td className="px-4 py-3 text-right">
                      <p className="text-sm text-white">{formatMoney(inv.total_minor, inv.currency)}</p>
                      {inv.tax_minor > 0 && (
                        <p className="text-[11px] text-gray-500">tax {formatMoney(inv.tax_minor, inv.currency)}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {inv.status === "issued" ? (
                        <span className="rounded bg-emerald-900/40 px-2 py-0.5 text-xs text-emerald-300">Issued</span>
                      ) : (
                        <span className="rounded bg-red-900/40 px-2 py-0.5 text-xs text-red-300" title={inv.void_reason ?? undefined}>
                          Cancelled
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <DeliveryBadge label="Email" attempt={inv.delivery?.email} />
                      <DeliveryBadge label="WA" attempt={inv.delivery?.whatsapp} />
                    </td>
                    <td className="px-4 py-3 text-right leading-6">
                      <button onClick={() => openPdf(inv.invoice_url)} className="mr-3 whitespace-nowrap text-xs text-indigo-400 hover:text-indigo-300">
                        Invoice PDF
                      </button>
                      <button onClick={() => openPdf(inv.receipt_url)} className="mr-3 whitespace-nowrap text-xs text-indigo-400 hover:text-indigo-300">
                        Receipt PDF
                      </button>
                      {inv.status === "issued" && (
                        <>
                          <span className="mr-3 inline-block whitespace-nowrap">
                            <ResendMenu invoice={inv} onSent={() => void loadInvoices()} />
                          </span>
                          <button onClick={() => void cancelInvoice(inv)} className="whitespace-nowrap text-xs text-red-400 hover:text-red-300">
                            Cancel
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-gray-800 px-5 py-3">
          <span className="text-xs text-gray-500">
            {total > 0 ? `Showing page ${page} of ${totalPages} · ${total.toLocaleString("en-IN")} total` : "No results"}
          </span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}
              className="rounded-lg bg-gray-800 px-3 py-1.5 text-sm text-gray-300 transition-colors hover:bg-gray-700 disabled:opacity-40">
              Previous
            </button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}
              className="rounded-lg bg-gray-800 px-3 py-1.5 text-sm text-gray-300 transition-colors hover:bg-gray-700 disabled:opacity-40">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
